import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "../hooks/useAuth";
import { ArrowLeft, Loader2, Save } from "lucide-react";
import { cn } from "@/lib/utils";
import { API_BASE } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

interface PropertyFormData {
  nome: string;
  codigo: string;
  tipoImovelId: string;
  logradouro: string;
  numero: string;
  complemento: string;
  bairro: string;
  cidade: string;
  uf: string;
  cep: string;
  areaUtil: string;
  areaTotal: string;
  quartos: string;
  banheiros: string;
  vagasGaragem: string;
  valorAluguel: string;
  valorCondominio: string;
  valorIptu: string;
  descricao: string;
  ativo: boolean;
}

const initialFormData: PropertyFormData = {
  nome: "",
  codigo: "",
  tipoImovelId: "",
  logradouro: "",
  numero: "",
  complemento: "",
  bairro: "",
  cidade: "",
  uf: "",
  cep: "",
  areaUtil: "",
  areaTotal: "",
  quartos: "",
  banheiros: "",
  vagasGaragem: "",
  valorAluguel: "",
  valorCondominio: "",
  valorIptu: "",
  descricao: "",
  ativo: true,
};

// Campos monetarios guardam apenas digitos (centavos); a mascara exibe em reais.
const onlyDigits = (v: string) => v.replace(/\D/g, "").slice(0, 11);

const formatBRL = (digits: string) =>
  (Number(digits || "0") / 100).toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export default function PropertyForm() {
  const { user } = useAuth();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [formData, setFormData] = useState<PropertyFormData>(initialFormData);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Partial<PropertyFormData>>({});
  const [tipos, setTipos] = useState<{ id: string; nome: string }[]>([]);

  const isEditing = Boolean(id);

  useEffect(() => {
    fetch(`${API_BASE}/api/tipos-imovel`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : { tipos: [] }))
      .then((d) => setTipos(d.tipos ?? []))
      .catch(() => setTipos([]));
    if (isEditing) {
      fetchProperty();
    }
  }, [id]);

  const fetchProperty = async () => {
    if (!user || !id) return;
    
    try {
      setLoading(true);
      const token = localStorage.getItem("auth_token");
      
      const res = await fetch(`${API_BASE}/api/imoveis/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (res.ok) {
        const data = await res.json();
        const property = data.imovel;
        
        setFormData({
          nome: property.nome || "",
          codigo: property.codigo || "",
          tipoImovelId: property.tipoImovelId || "",
          logradouro: property.logradouro || "",
          numero: property.numero || "",
          complemento: property.complemento || "",
          bairro: property.bairro || "",
          cidade: property.cidade || "",
          uf: property.uf || "",
          cep: property.cep || "",
          areaUtil: property.areaUtil?.toString() || "",
          areaTotal: property.areaTotal?.toString() || "",
          quartos: property.quartos?.toString() || "",
          banheiros: property.banheiros?.toString() || "",
          vagasGaragem: property.vagasGaragem?.toString() || "",
          valorAluguel: property.valorAluguel?.toString() || "",
          valorCondominio: property.valorCondominio?.toString() || "",
          valorIptu: property.valorIptu?.toString() || "",
          descricao: property.descricao || "",
          ativo: property.ativo !== false,
        });
      }
    } catch (err) {
      console.error("Failed to fetch property:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === "checkbox" ? (e.target as HTMLInputElement).checked : value }));
    if (errors[name as keyof PropertyFormData]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleChangeBRL =
    (name: "valorAluguel" | "valorCondominio" | "valorIptu") =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormData(prev => ({ ...prev, [name]: onlyDigits(e.target.value) }));
    };

  const validateForm = (): boolean => {
    const newErrors: Partial<PropertyFormData> = {};
    
    if (!formData.nome.trim()) newErrors.nome = "Nome do imóvel é obrigatório";
    if (!formData.codigo.trim()) newErrors.codigo = "Código é obrigatório";
    if (!formData.tipoImovelId.trim()) newErrors.tipoImovelId = "Tipo de imóvel é obrigatório";
    if (!formData.logradouro.trim()) newErrors.logradouro = "Logradouro é obrigatório";
    if (!formData.numero.trim()) newErrors.numero = "Número é obrigatório";
    if (!formData.bairro.trim()) newErrors.bairro = "Bairro é obrigatório";
    if (!formData.cidade.trim()) newErrors.cidade = "Cidade é obrigatória";
    if (!formData.uf.trim()) newErrors.uf = "UF é obrigatória";
    if (!formData.cep.trim()) newErrors.cep = "CEP é obrigatório";
    if (!formData.valorAluguel.trim()) newErrors.valorAluguel = "Valor do aluguel é obrigatório";
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    if (!user) return;
    
    try {
      setSaving(true);
      const token = localStorage.getItem("auth_token");
      const apiUrl = API_BASE;
      
      const payload = {
        ...formData,
        // digitos da mascara ja estao em centavos
        valorAluguel: parseInt(formData.valorAluguel || "0", 10),
        valorCondominio: formData.valorCondominio ? parseInt(formData.valorCondominio, 10) : null,
        valorIptu: formData.valorIptu ? parseInt(formData.valorIptu, 10) : null,
        areaUtil: formData.areaUtil ? parseFloat(formData.areaUtil) : null,
        areaTotal: formData.areaTotal ? parseFloat(formData.areaTotal) : null,
        quartos: formData.quartos ? parseInt(formData.quartos) : null,
        banheiros: formData.banheiros ? parseInt(formData.banheiros) : null,
        vagasGaragem: formData.vagasGaragem ? parseInt(formData.vagasGaragem) : null,
        complemento: formData.complemento || null,
        descricao: formData.descricao || null,
      };
      
      const url = isEditing 
        ? `${apiUrl}/api/imoveis/${id}`
        : `${apiUrl}/api/imoveis`;
      
      const res = await fetch(url, {
        method: isEditing ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      
      if (res.ok) {
        toast.success(isEditing ? "Im�vel atualizado" : "Im�vel cadastrado");
        navigate("/properties");
      } else {
        const errorData = await res.json();
        toast.error(errorData.error || "Erro ao salvar imóvel");
      }
    } catch (err) {
      console.error("Failed to save property:", err);
      toast.error("Erro ao salvar imóvel");
    } finally {
      setSaving(false);
    }
  };

  if (!user) return <div className="flex items-center justify-center h-screen">Carregando...</div>;

  if (loading) {
    return (
      <div className="min-h-screen bg-background font-sans flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background font-sans">
      <div className="max-w-3xl mx-auto p-6">
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-3xl font-bold">{isEditing ? "Editar Imóvel" : "Novo Imóvel"}</h1>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Informações do Imóvel</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="nome">Nome do Imóvel *</Label>
                <Input
                  id="nome"
                  name="nome"
                  value={formData.nome}
                  onChange={handleChange}
                  placeholder="Ex: Residencial Batel, Comercial Centro"
                  maxLength={120}
                  className={cn(errors.nome && "border-destructive")}
                />
                {errors.nome && <p className="text-sm text-destructive">{errors.nome}</p>}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="codigo">Código *</Label>
                  <Input
                    id="codigo"
                    name="codigo"
                    value={formData.codigo}
                    onChange={handleChange}
                    className={cn(errors.codigo && "border-destructive")}
                    disabled={isEditing}
                  />
                  {errors.codigo && <p className="text-sm text-destructive">{errors.codigo}</p>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tipoImovelId">Tipo de Imóvel *</Label>
                  <Select
                    value={formData.tipoImovelId || undefined}
                    onValueChange={(v) => setFormData((prev) => ({ ...prev, tipoImovelId: v }))}
                  >
                    <SelectTrigger
                      id="tipoImovelId"
                      className={cn(errors.tipoImovelId && "border-destructive")}
                    >
                      <SelectValue placeholder="Selecione o tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      {tipos.map((t) => (
                        <SelectItem key={t.id} value={t.id}>
                          {t.nome}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.tipoImovelId && <p className="text-sm text-destructive">{errors.tipoImovelId}</p>}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="logradouro">Logradouro *</Label>
                <Input
                  id="logradouro"
                  name="logradouro"
                  value={formData.logradouro}
                  onChange={handleChange}
                  placeholder="Rua, Avenida, etc."
                  className={cn(errors.logradouro && "border-destructive")}
                />
                {errors.logradouro && <p className="text-sm text-destructive">{errors.logradouro}</p>}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="numero">Número *</Label>
                  <Input
                    id="numero"
                    name="numero"
                    value={formData.numero}
                    onChange={handleChange}
                    className={cn(errors.numero && "border-destructive")}
                  />
                  {errors.numero && <p className="text-sm text-destructive">{errors.numero}</p>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="complemento">Complemento</Label>
                  <Input
                    id="complemento"
                    name="complemento"
                    value={formData.complemento}
                    onChange={handleChange}
                    placeholder="Apto, Bloco, etc."
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bairro">Bairro *</Label>
                  <Input
                    id="bairro"
                    name="bairro"
                    value={formData.bairro}
                    onChange={handleChange}
                    className={cn(errors.bairro && "border-destructive")}
                  />
                  {errors.bairro && <p className="text-sm text-destructive">{errors.bairro}</p>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cep">CEP *</Label>
                  <Input
                    id="cep"
                    name="cep"
                    value={formData.cep}
                    onChange={handleChange}
                    placeholder="00000-000"
                    className={cn(errors.cep && "border-destructive")}
                  />
                  {errors.cep && <p className="text-sm text-destructive">{errors.cep}</p>}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="cidade">Cidade *</Label>
                  <Input
                    id="cidade"
                    name="cidade"
                    value={formData.cidade}
                    onChange={handleChange}
                    className={cn(errors.cidade && "border-destructive")}
                  />
                  {errors.cidade && <p className="text-sm text-destructive">{errors.cidade}</p>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="uf">UF *</Label>
                  <Input
                    id="uf"
                    name="uf"
                    value={formData.uf}
                    onChange={handleChange}
                    maxLength={2}
                    placeholder="SP"
                    className={cn(errors.uf && "border-destructive")}
                  />
                  {errors.uf && <p className="text-sm text-destructive">{errors.uf}</p>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="areaUtil">Área Útil (m²)</Label>
                  <Input
                    id="areaUtil"
                    name="areaUtil"
                    type="number"
                    step="0.01"
                    value={formData.areaUtil}
                    onChange={handleChange}
                    placeholder="Ex: 65.50"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="areaTotal">Área Total (m²)</Label>
                  <Input
                    id="areaTotal"
                    name="areaTotal"
                    type="number"
                    step="0.01"
                    value={formData.areaTotal}
                    onChange={handleChange}
                    placeholder="Ex: 80.00"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="quartos">Quartos</Label>
                  <Input
                    id="quartos"
                    name="quartos"
                    type="number"
                    value={formData.quartos}
                    onChange={handleChange}
                    placeholder="Ex: 2"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="banheiros">Banheiros</Label>
                  <Input
                    id="banheiros"
                    name="banheiros"
                    type="number"
                    value={formData.banheiros}
                    onChange={handleChange}
                    placeholder="Ex: 1"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="vagasGaragem">Vagas</Label>
                  <Input
                    id="vagasGaragem"
                    name="vagasGaragem"
                    type="number"
                    value={formData.vagasGaragem}
                    onChange={handleChange}
                    placeholder="Ex: 1"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="valorAluguel">Valor Aluguel (R$) *</Label>
                  <Input
                    id="valorAluguel"
                    name="valorAluguel"
                    type="text"
                    inputMode="numeric"
                    value={formatBRL(formData.valorAluguel)}
                    onChange={handleChangeBRL("valorAluguel")}
                    placeholder="0,00"
                    className={cn(errors.valorAluguel && "border-destructive")}
                  />
                  {errors.valorAluguel && <p className="text-sm text-destructive">{errors.valorAluguel}</p>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="valorCondominio">Condomínio (R$)</Label>
                  <Input
                    id="valorCondominio"
                    name="valorCondominio"
                    type="text"
                    inputMode="numeric"
                    value={formatBRL(formData.valorCondominio)}
                    onChange={handleChangeBRL("valorCondominio")}
                    placeholder="0,00"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="valorIptu">IPTU (R$)</Label>
                  <Input
                    id="valorIptu"
                    name="valorIptu"
                    type="text"
                    inputMode="numeric"
                    value={formatBRL(formData.valorIptu)}
                    onChange={handleChangeBRL("valorIptu")}
                    placeholder="0,00"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="descricao">Descrição</Label>
                <textarea
                  id="descricao"
                  name="descricao"
                  value={formData.descricao}
                  onChange={handleChange}
                  rows={4}
                  className="w-full px-3 py-2 border border-input bg-background rounded-md text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  placeholder="Observações adicionais..."
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="ativo"
                  name="ativo"
                  checked={formData.ativo}
                  onChange={handleChange}
                  className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                />
                <Label htmlFor="ativo" className="cursor-pointer">Ativo</Label>
              </div>

              <div className="flex justify-end gap-4 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => navigate(-1)}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={saving}>
                  <Save className="mr-2 h-4 w-4" />
                  {saving ? "Salvando..." : (isEditing ? "Atualizar" : "Criar")}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
